import { Suspense, lazy, type ComponentType } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './stores/authStore';
import { ApiError } from './lib/api';
import { useApplyTheme } from './lib/theme';
import { ToastContainer } from './components/ui/Toast';
import { AppLayout } from './components/layout/AppLayout';
import { ErrorBoundary } from './components/ErrorBoundary';

/**
 * Route-level code splitting: each page ships as its own chunk so the
 * landing/login screens load without pulling in charting and editor libs.
 */
const page = <K extends string>(loader: () => Promise<Record<K, ComponentType>>, name: K) =>
  lazy(() => loader().then((m) => ({ default: m[name] })));

const LandingPage = page(() => import('./pages/landing/LandingPage'), 'LandingPage');
const LoginPage = page(() => import('./pages/auth/LoginPage'), 'LoginPage');
const RegisterPage = page(() => import('./pages/auth/RegisterPage'), 'RegisterPage');
const DashboardPage = page(() => import('./pages/dashboard/DashboardPage'), 'DashboardPage');
const JournalPage = page(() => import('./pages/journal/JournalPage'), 'JournalPage');
const ReportsPage = page(() => import('./pages/reports/ReportsPage'), 'ReportsPage');
const ReplayPage = page(() => import('./pages/replay/ReplayPage'), 'ReplayPage');
const BacktestPage = page(() => import('./pages/backtest/BacktestPage'), 'BacktestPage');
const PlaybooksPage = page(() => import('./pages/playbooks/PlaybooksPage'), 'PlaybooksPage');
const NotebookPage = page(() => import('./pages/notebook/NotebookPage'), 'NotebookPage');
const MentorPage = page(() => import('./pages/mentor/MentorPage'), 'MentorPage');
const EducationPage = page(() => import('./pages/education/EducationPage'), 'EducationPage');
const SettingsPage = page(() => import('./pages/settings/SettingsPage'), 'SettingsPage');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Do not hammer the API for client errors (4xx); retry transient failures once.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 1;
      },
    },
  },
});

function Spinner({ fullScreen = false }: { fullScreen?: boolean }) {
  return (
    <div className={fullScreen ? 'flex items-center justify-center h-screen bg-dark-bg' : 'flex items-center justify-center h-64'}>
      <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
    </div>
  );
}

/** Each page gets its own boundary so an error in one view leaves the shell usable. */
function PageBoundary() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<Spinner />}>
        <Outlet />
      </Suspense>
    </ErrorBoundary>
  );
}

function AppRoutes() {
  // Auth state is fed by <AuthBridge /> (rendered inside PrivyProvider in main.tsx).
  const loading = useAuthStore((s) => s.loading);
  useApplyTheme();

  if (loading) return <Spinner fullScreen />;

  return (
    <Suspense fallback={<Spinner fullScreen />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route element={<AppLayout />}>
          <Route element={<PageBoundary />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/journal" element={<JournalPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/replay" element={<ReplayPage />} />
            <Route path="/backtest" element={<BacktestPage />} />
            <Route path="/playbooks" element={<PlaybooksPage />} />
            <Route path="/notebook" element={<NotebookPage />} />
            <Route path="/mentor" element={<MentorPage />} />
            <Route path="/education" element={<EducationPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppRoutes />
          <ToastContainer />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
